import React, { useState, useEffect } from 'react'

// Utils
import { useAppState } from '../context/AppContext'

// Types
import { IFilter, getImage } from '../components/TeamFilters'
import { TeamRole } from '../common/enums/userCompanyRole.enum'
import { ICompanyMember, ICompanyTeam } from '../common/models/Company'
import { ICompanyMetadataMemberPure } from '../common/models/CompanyMetadata'
import { notEmpty } from '../utils/typeGuards'
import { getMemberRole } from '../common/helpers/isLeadOf'

type GetActiveEmployees = (
  searchTerm: string,
  members: ICompanyMember[],
  setActiveEmployees: React.Dispatch<React.SetStateAction<ICompanyMember[]>>,
  selectedFilter?: IFilter
) => void

export interface IFiltersObject {
  selectedFilter: IFilter | undefined
  staffFilters: IFilter[]
  activeEmployees: ICompanyMember[]
  setSelectedFilter: React.Dispatch<React.SetStateAction<IFilter | undefined>>
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>
  searchTerm: string
  resetSearchForm: () => void
}

type UseFilters = (
  members?: ICompanyMember[],
  companyTeams?: ICompanyTeam[],
  suggestedMembers?: ICompanyMetadataMemberPure,
  extraStaffFilter?: IFilter,
  dependencies?: any[]
) => IFiltersObject

// Constants
export const ALL_FILTER = 'useTeamFilters.labelEveryone'
export const LEAD_FILTER = 'useTeamFilters.labelLeads'
export const ADMIN_FILTER = 'useTeamFilters.labelAdmins'
export const SUGGESTED_FILTER = 'useTeamFilters.labelSuggested'
export const NO_TEAM = 'useTeamFilters.labelNotInTeam'
export const ALL_LEVELS = 'useTeamFilters.labelAllLevels'

// Helpers
const transformString = (str: string) =>
  str
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')

const getActiveEmployees: GetActiveEmployees = (
  searchTerm,
  members,
  setActiveEmployees,
  selectedFilter
) => {
  let people = members
  if (selectedFilter && selectedFilter.members) people = selectedFilter.members

  const activeMembers = people.filter(
    (employee) =>
      searchTerm === '' ||
      transformString(employee.name).includes(transformString(searchTerm))
  )

  setActiveEmployees(activeMembers)
}

const useFilters: UseFilters = (
  members,
  companyTeams,
  suggestedMembers,
  extraStaffFilter,
  dependencies = []
) => {
  const appState = useAppState()

  const company = appState.company
  const companyTeamsRaw = company?.teams || []
  const membersRaw = company?.members || []

  const [searchTerm, setSearchTerm] = useState<string>('')
  const [selectedFilter, setSelectedFilter] = useState<IFilter>()
  const [staffFilters, setStaffFilters] = useState<IFilter[]>([])
  const [activeEmployees, setActiveEmployees] = useState<ICompanyMember[]>([])

  const companyTeamsToBeFiltered = companyTeams || companyTeamsRaw
  const membersToBeFiltered = members || membersRaw

  const resetSearchForm = () => {
    setSearchTerm('')
    const form: HTMLFormElement | null = document?.getElementById(
      'searchInput'
    ) as HTMLFormElement
    if (form) form.reset()
  }

  useEffect(() => {
    const staff: IFilter[] = []

    if (membersToBeFiltered.length === 0) return

    staff.push({
      name: ALL_FILTER,
      members: membersToBeFiltered.filter((member) =>
        searchTerm === ''
          ? member
          : member.name.toLowerCase().includes(searchTerm.toLowerCase())
      ),
      icon: getImage(ALL_FILTER),
    })

    if (suggestedMembers) {
      const closeMembers = suggestedMembers.closeMembersIds
        .map((id) => {
          return membersToBeFiltered.find((member) => id === member._id)
        })
        .filter(notEmpty)

      if (closeMembers) {
        staff.push({
          name: SUGGESTED_FILTER,
          members:
            searchTerm === ''
              ? closeMembers
              : closeMembers.filter((member) =>
                  member?.name.toLowerCase().includes(searchTerm.toLowerCase())
                ),
        })
      }
    }

    const admins = membersToBeFiltered.filter(
      (member) =>
        member.isAdmin &&
        (searchTerm === ''
          ? member
          : member.name.toLowerCase().includes(searchTerm.toLowerCase()))
    )

    staff.push({
      name: ADMIN_FILTER,
      members: admins,
      icon: getImage(ADMIN_FILTER),
    })

    const teamLeads = membersToBeFiltered.filter(
      (member) =>
        getMemberRole(member) === TeamRole.LEAD &&
        (searchTerm === ''
          ? member
          : member.name.toLowerCase().includes(searchTerm.toLowerCase()))
    )

    staff.push({
      name: LEAD_FILTER,
      members: teamLeads,
      icon: getImage(LEAD_FILTER),
      separator: true,
    })

    if (extraStaffFilter) {
      staff.push(extraStaffFilter)
    }

    if (companyTeamsToBeFiltered.length === 0) return

    companyTeamsToBeFiltered.forEach((team, index) => {
      const filteredTeamMembers = membersToBeFiltered.filter((member) =>
        member.teams.find((t) => t.teamId === team._id)
      )

      staff.push({
        name: team.name,
        members:
          searchTerm === ''
            ? filteredTeamMembers
            : filteredTeamMembers.filter((member) =>
                member.name.toLowerCase().includes(searchTerm.toLowerCase())
              ),
        icon: team.icon,
        type: 'team',
        separator: index === companyTeamsToBeFiltered.length - 1,
      })
    })

    if (
      membersToBeFiltered.filter(
        (member) => !member.teams || member.teams.length === 0
      ).length > 0
    ) {
      staff.push({
        name: NO_TEAM,
        members: membersToBeFiltered.filter(
          (member) =>
            (!member.teams || member.teams.length === 0) &&
            (searchTerm === ''
              ? member
              : member.name.toLowerCase().includes(searchTerm.toLowerCase()))
        ),
      })
    }

    setStaffFilters(staff)

    // if no selectedFilter set, set the first of all filters
    setSelectedFilter((prevState) => {
      if (!prevState) return staff[0]

      // update current selectedFilter with possibly updated version
      const allFilters = [...staff]

      return allFilters.find((filter) => filter.name === prevState.name)
    })
  }, [
    searchTerm,
    membersToBeFiltered,
    companyTeamsToBeFiltered,
    suggestedMembers,
    ...dependencies,
  ])

  useEffect(() => {
    if (!selectedFilter || membersToBeFiltered.length === 0) {
      return
    }

    getActiveEmployees(
      searchTerm,
      membersToBeFiltered,
      setActiveEmployees,
      selectedFilter
    )
  }, [selectedFilter, searchTerm])

  return {
    selectedFilter,
    staffFilters,
    activeEmployees,
    setSelectedFilter,
    setSearchTerm,
    searchTerm,
    resetSearchForm,
  }
}

export default useFilters
